import type {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  RaRecord,
  UpdateParams,
  UpdateResult,
} from 'react-admin';

import type { HttpClient } from '@/api/httpClient';
import { asDisplayString, asIdString } from '@/formatting/text';
import { RESOURCE_CATALOG } from '@/resources/catalog';
import { isBabyScoped, resolvePath, type ResourceDescriptor } from '@/resources/descriptors';

import { applyClientFilters, applyClientPagination, applyClientSort } from './collection';

/**
 * `dataProvider` React Admin pose sur les controleurs `#[Route]` reels.
 *
 * Il traduit le vocabulaire de React Admin (`getList`, `getOne`, `create`, …)
 * vers celui de l'API : enveloppe `{items}`, pagination `limit`/`offset` sans
 * total, cle d'idempotence dans le corps, routes de creation multiples.
 *
 * Deux principes le gouvernent :
 *
 * 1. **Ne jamais inventer une operation.** Une methode qu'aucune route ne sert
 *    leve une erreur en francais, elle ne repond pas un succes vide. React Admin
 *    l'affiche comme une notification, l'utilisateur comprend, rien n'est perdu
 *    en silence.
 * 2. **Ne jamais inventer un total.** Les routes paginees rendent `hasMore`, pas
 *    un nombre d'elements. On repond donc `pageInfo`, que React Admin sait
 *    exploiter, plutot qu'un total devine.
 *
 * **Sur les conversions `as unknown as RecordType`.** Les methodes de
 * `DataProvider` sont declarees `<RecordType extends RaRecord>(…) =>
 * Promise<{data: RecordType}>`, ou `RecordType` est choisi par l'appelant. Un
 * `dataProvider` lit du JSON : il ne peut rien produire de plus precis que
 * `Record<string, unknown>`, et TypeScript refuse a juste titre de le tenir pour
 * assignable a un type que seul l'appelant connait. C'est une limite de variance
 * des signatures de React Admin, non un defaut de forme — la structure rendue est
 * bien celle attendue. La conversion est donc explicite a chaque retour, jamais
 * appliquee a l'objet entier : un `as DataProvider` global masquerait l'oubli
 * d'une methode.
 */

/** Enveloppe de collection commune a toutes les routes de liste. */
interface CollectionResponse {
  readonly items?: unknown;
  readonly hasMore?: unknown;
  readonly limit?: unknown;
  readonly offset?: unknown;
}

const asRecords = (payload: unknown): Record<string, unknown>[] => {
  if (typeof payload !== 'object' || payload === null) {
    return [];
  }

  const items = (payload as CollectionResponse).items;

  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(
    (item): item is Record<string, unknown> => typeof item === 'object' && item !== null,
  );
};

const asRecord = (payload: unknown): Record<string, unknown> => {
  if (typeof payload !== 'object' || payload === null) {
    throw new Error("La réponse de l'API n'est pas un objet exploitable.");
  }

  // Les creations de lots repondent `{items: [...]}` meme pour un seul lot : le
  // service peut repartir un tirage sur plusieurs emplacements. On prend le
  // premier, qui est celui que le formulaire vient de creer.
  const items = (payload as CollectionResponse).items;

  if (Array.isArray(items)) {
    const first: unknown = items[0];

    if (typeof first === 'object' && first !== null) {
      return first as Record<string, unknown>;
    }

    throw new Error("L'API a répondu une collection vide à une création.");
  }

  return payload as Record<string, unknown>;
};

/** Erreur levee quand une operation n'est pas servie par l'API. */
const unsupported = (descriptor: ResourceDescriptor, operation: string): Error =>
  new Error(
    `L'API n'expose pas l'opération « ${operation} » pour « ${descriptor.label} ». ` +
      'Cette limite vient du serveur, pas du backoffice.',
  );

const requireDescriptor = (catalog: readonly ResourceDescriptor[], resource: string): ResourceDescriptor => {
  const descriptor = catalog.find((candidate) => candidate.name === resource);

  if (descriptor === undefined) {
    throw new Error(`Ressource inconnue du catalogue : « ${resource} ».`);
  }

  return descriptor;
};

/** Ne conserve que les filtres que la route interprete reellement. */
const serverQuery = (
  descriptor: ResourceDescriptor,
  filter: Readonly<Record<string, unknown>>,
): Record<string, string> => {
  const query: Record<string, string> = {};

  for (const key of descriptor.serverFilters) {
    const value = filter[key];

    if (value !== undefined && value !== null && value !== '') {
      query[key] = asDisplayString(value);
    }
  }

  return query;
};

export interface RestDataProviderDependencies {
  readonly http: HttpClient;
  /** Profil bebe courant, relu a chaque appel : il change sans remonter le provider. */
  readonly currentBabyId: () => string | null;
  /**
   * Fabrique de cles d'idempotence, injectee pour rester testable.
   * `crypto.randomUUID` par defaut.
   */
  readonly newIdempotencyKey?: () => string;
  /**
   * Ressources servies. `RESOURCE_CATALOG` par defaut ; un test injecte la
   * sienne pour exercer ce que le catalogue reel ne decrit plus (chemin porte par
   * un profil bebe, pagination serveur, deux routes de creation).
   */
  readonly catalog?: readonly ResourceDescriptor[];
}

export const createRestDataProvider = ({
  http,
  currentBabyId,
  newIdempotencyKey = () => globalThis.crypto.randomUUID(),
  catalog = RESOURCE_CATALOG,
}: RestDataProviderDependencies): DataProvider => {
  const basePath = (descriptor: ResourceDescriptor): string =>
    resolvePath(descriptor, currentBabyId());

  /** Charge la collection entiere. Reserve aux ressources `pagination: 'client'`. */
  const fetchAll = async (
    descriptor: ResourceDescriptor,
    filter: Readonly<Record<string, unknown>>,
  ): Promise<Record<string, unknown>[]> =>
    asRecords(
      await http.request({
        path: basePath(descriptor),
        query: serverQuery(descriptor, filter),
      }),
    );

  const findInCollection = async (
    descriptor: ResourceDescriptor,
    id: string,
  ): Promise<Record<string, unknown>> => {
    const records = await fetchAll(descriptor, {});
    const match = records.find((record) => asIdString(record.id) === id);

    if (match === undefined) {
      throw new Error(
        `Aucun enregistrement « ${id} » dans « ${descriptor.label} ». ` +
          "Il a peut-être été archivé, ou il n'appartient pas au profil bébé sélectionné.",
      );
    }

    return match;
  };

  const getOneRecord = async (
    descriptor: ResourceDescriptor,
    id: string,
  ): Promise<Record<string, unknown>> => {
    if (descriptor.singleRecord === 'endpoint') {
      return asRecord(await http.request({ path: `${basePath(descriptor)}/${encodeURIComponent(id)}` }));
    }

    return findInCollection(descriptor, id);
  };

  return {
    getList: async <RecordType extends RaRecord>(
      resource: string,
      params: GetListParams,
    ): Promise<GetListResult<RecordType>> => {
      const descriptor = requireDescriptor(catalog, resource);
      const filter = (params.filter ?? {}) as Record<string, unknown>;

      if (descriptor.pagination === 'server') {
        // Valeurs de repli alignees sur le `perPage` des ecrans de liste.
        const page = params.pagination?.page ?? 1;
        const perPage = params.pagination?.perPage ?? 25;

        const payload = await http.request<CollectionResponse>({
          path: basePath(descriptor),
          query: {
            ...serverQuery(descriptor, filter),
            limit: perPage,
            offset: (page - 1) * perPage,
          },
        });

        // Aucun total n'est disponible : `pageInfo` est la reponse prevue par
        // React Admin pour ce cas. Renvoyer un total calcule depuis la page
        // courante afficherait « 1-25 sur 25 » sur une collection de mille.
        return {
          data: asRecords(payload) as unknown as RecordType[],
          pageInfo: {
            hasNextPage: payload.hasMore === true,
            hasPreviousPage: page > 1,
          },
        };
      }

      const all = await fetchAll(descriptor, filter);
      const filtered = applyClientFilters(all, filter, descriptor.serverFilters);
      const sorted = applyClientSort(filtered, params.sort);
      const { data, total } = applyClientPagination(sorted, {
        page: params.pagination?.page ?? 1,
        perPage: params.pagination?.perPage ?? 25,
      });

      return { data: data as unknown as RecordType[], total };
    },

    getOne: async <RecordType extends RaRecord>(
      resource: string,
      params: GetOneParams,
    ): Promise<GetOneResult<RecordType>> => {
      const descriptor = requireDescriptor(catalog, resource);

      return {
        data: (await getOneRecord(descriptor, asIdString(params.id))) as unknown as RecordType,
      };
    },

    /**
     * Resolution de plusieurs identifiants, utilisee par les champs de
     * reference. Une seule requete de collection suffit : les identifiants
     * demandes en sont extraits.
     */
    getMany: async <RecordType extends RaRecord>(
      resource: string,
      params: GetManyParams,
    ): Promise<GetManyResult<RecordType>> => {
      const descriptor = requireDescriptor(catalog, resource);
      const wanted = new Set(params.ids.map((id) => String(id)));
      const records = await fetchAll(descriptor, {});

      return {
        data: records.filter((record) =>
          wanted.has(asIdString(record.id)),
        ) as unknown as RecordType[],
      };
    },

    getManyReference: async <RecordType extends RaRecord>(
      resource: string,
      params: GetManyReferenceParams,
    ): Promise<GetManyReferenceResult<RecordType>> => {
      const descriptor = requireDescriptor(catalog, resource);
      const filter: Record<string, unknown> = {
        ...((params.filter ?? {}) as Record<string, unknown>),
        [params.target]: params.id,
      };
      const all = await fetchAll(descriptor, filter);
      const filtered = applyClientFilters(all, filter, descriptor.serverFilters);
      const sorted = applyClientSort(filtered, params.sort);
      const { data, total } = applyClientPagination(sorted, params.pagination);

      return { data: data as unknown as RecordType[], total };
    },

    create: async <RecordType extends Omit<RaRecord, 'id'>, ResultRecordType extends RaRecord>(
      resource: string,
      params: CreateParams<RecordType>,
    ): Promise<CreateResult<ResultRecordType>> => {
      const descriptor = requireDescriptor(catalog, resource);

      if (!descriptor.capabilities.create) {
        throw unsupported(descriptor, 'création');
      }

      // Les champs pilotant le formulaire sans exister cote serveur sont ecartes
      // par reconstruction : un `delete` sur une cle calculee deoptimise l'objet et
      // se lit moins bien qu'un filtre explicite.
      const omitted = new Set(descriptor.omitOnCreate ?? []);
      const values: Record<string, unknown> = Object.fromEntries(
        Object.entries(params.data as Record<string, unknown>).filter(
          ([key]) => !omitted.has(key),
        ),
      );

      // La cle d'idempotence est generee ici et non dans le formulaire : elle
      // doit changer a chaque tentative d'envoi, pas a chaque frappe. Le projet
      // la transporte dans le corps JSON, jamais dans un en-tete.
      if (descriptor.requiresIdempotencyKey === true) {
        values.idempotencyKey = newIdempotencyKey();
      }

      const suffix = descriptor.createSuffix?.(params.data);
      const path = suffix === undefined ? basePath(descriptor) : `${basePath(descriptor)}/${suffix}`;

      return {
        data: asRecord(
          await http.request({ method: 'POST', path, body: values }),
        ) as unknown as ResultRecordType,
      };
    },

    update: async <RecordType extends RaRecord>(
      resource: string,
      params: UpdateParams,
    ): Promise<UpdateResult<RecordType>> => {
      const descriptor = requireDescriptor(catalog, resource);

      if (!descriptor.capabilities.edit) {
        throw unsupported(descriptor, 'modification');
      }

      const payload = await http.request({
        method: 'PATCH',
        path: `${basePath(descriptor)}/${encodeURIComponent(String(params.id))}`,
        body: params.data,
      });

      return { data: asRecord(payload) as unknown as RecordType };
    },

    /**
     * Aucune ressource n'accepte une mise a jour de masse : le faire en bouclant
     * masquerait un echec partiel derriere un succes global, et le stock ne
     * tolere pas cela.
     */
    updateMany: (resource) => Promise.reject(unsupported(requireDescriptor(catalog, resource), 'modification en masse')),

    /**
     * Aucune route `DELETE` n'existe sur ces ressources, et c'est deliberé :
     * la tracabilite interdit la suppression (ADR 0011). L'equivalent est
     * l'archivage, propose comme action dediee.
     */
    delete: (resource) =>
      Promise.reject(
        new Error(
          `« ${requireDescriptor(catalog, resource).label} » ne se supprime pas : la traçabilité l'interdit. ` +
            "Utilisez l'action « Archiver » lorsqu'elle est proposée.",
        ),
      ),

    deleteMany: (resource) =>
      Promise.reject(
        new Error(
          `« ${requireDescriptor(catalog, resource).label} » ne se supprime pas : la traçabilité l'interdit.`,
        ),
      ),
  };
};

/**
 * Declenche une action non-CRUD (`archive`, `restore`) et rend l'enregistrement
 * mis a jour.
 *
 * Hors de l'interface `DataProvider` a dessein : ce n'est pas une operation CRUD,
 * et l'y glisser via `dataProvider.archive` la rendrait invisible au typage.
 */
export const runResourceAction = async (
  http: HttpClient,
  descriptor: ResourceDescriptor,
  babyId: string | null,
  id: string,
  suffix: string,
  method: 'POST' | 'DELETE',
): Promise<Record<string, unknown>> => {
  const path = `${resolvePath(descriptor, babyId)}/${encodeURIComponent(id)}/${suffix}`;

  return asRecord(await http.request({ method, path }));
};

/** Une ressource portee par un bebe est inutilisable sans profil selectionne. */
export const requiresBabySelection = (
  descriptor: ResourceDescriptor,
  babyId: string | null,
): boolean => isBabyScoped(descriptor) && (babyId === null || babyId === '');
